// client/src/components/media/MusicPicker.jsx
//
// Modal picker for the "music on posts" feature. Used by MediaPost, QuickPost
// and MediaEditor. Talks to /api/music for search and /api/music/attach to
// associate the chosen track with a draft (or directly with a social_post).
//
// Props:
//   open                boolean
//   onClose             () => void
//   draftRef            string  — stable client-side id for this draft post
//   sourceVideoUrl      string  — the video to mix the track into
//   selectedPlatforms   string[] — drives per-track ✅/⚠/🚫 badges
//   currentAttachmentId number  — if set, shows the active attachment for re-edit
//   onAttached          ({ attachmentId, track, params }) => void
//
// The picker creates the attachment row immediately on confirm. The mix
// worker starts processing it in the background; the composer polls
// /api/music/attachment/:id to flip a "mixing… → ready" badge.

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../../api/client';

const COMPAT_LABEL = {
  safe:       { icon: '✅', label: 'Licensed for this platform' },
  may_claim:  { icon: '⚠',  label: 'May trigger a content claim or muting' },
  blocked:    { icon: '🚫', label: 'Not permitted on this platform' },
  unknown:    { icon: '❓', label: 'License compatibility unknown' },
};

function parseCompat(v) {
  if (!v) return {};
  if (typeof v === 'object') return v;
  try { return JSON.parse(v); } catch { return {}; }
}

function compatBadgesFor(track, selectedPlatforms) {
  const compat = parseCompat(track.platform_compat);
  return selectedPlatforms.map(p => ({
    platform: p,
    state: compat[p] || 'unknown',
  }));
}

export default function MusicPicker({
  open,
  onClose,
  draftRef,
  sourceVideoUrl,
  selectedPlatforms = [],
  currentAttachmentId,
  onAttached,
}) {
  const [query, setQuery] = useState('');
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [order, setOrder] = useState('popular');

  const [selected, setSelected] = useState(null);
  const [volume, setVolume] = useState(0.8);
  const [fadeIn, setFadeIn] = useState(1);
  const [fadeOut, setFadeOut] = useState(1);
  const [duckVideo, setDuckVideo] = useState(true);
  const [useTikTokNative, setUseTikTokNative] = useState(false);
  const [attaching, setAttaching] = useState(false);

  const audioRef = useRef(null);

  const tiktokSelected = selectedPlatforms.includes('tiktok');

  const runSearch = useCallback(async (q, sortOrder = order) => {
    setLoading(true);
    setErrorMsg('');
    try {
      const params = { q, limit: 50, order: sortOrder };
      if (selectedPlatforms.length) params.platforms = selectedPlatforms.join(',');
      const { data } = await api.get('/music/search', { params });
      setTracks(Array.isArray(data?.tracks) ? data.tracks : []);
    } catch (err) {
      console.error('[MusicPicker] search failed', err);
      setErrorMsg(err?.response?.data?.error || 'Search failed');
      setTracks([]);
    } finally {
      setLoading(false);
    }
  }, [selectedPlatforms, order]);

  useEffect(() => {
    if (!open) return;
    runSearch(query);
    // Re-run whenever sort order or modal-open state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order]);

  useEffect(() => {
    if (!open) {
      try { audioRef.current?.pause(); } catch { /* noop */ }
      setSelected(null);
      setUseTikTokNative(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSearchKey = (e) => {
    if (e.key === 'Enter') runSearch(query);
  };

  const playPreview = (track) => {
    try { audioRef.current?.pause(); } catch { /* noop */ }
    const url = track.preview_url || track.download_url;
    if (!url) return;
    const a = new Audio(url);
    audioRef.current = a;
    a.volume = 0.7;
    a.play().catch(() => { /* autoplay blocked */ });
  };

  const handleAttach = async () => {
    console.debug('[MusicPicker] attach clicked', {
      selectedId: selected?.id, useTikTokNative, hasSourceVideo: !!sourceVideoUrl, draftRef,
    });
    if (!selected && !useTikTokNative) {
      setErrorMsg('Pick a track first (or enable TikTok native sound).');
      return;
    }
    if (!sourceVideoUrl) {
      setErrorMsg('Add a video to the post before attaching music. Music can only be added to video posts.');
      return;
    }
    setAttaching(true);
    setErrorMsg('');
    try {
      const body = useTikTokNative
        ? {
            // For TikTok-native we still need a real track row to satisfy the
            // FK; we let the user pick any free track (sentinel) — but if none
            // is selected we pick the first result.
            trackId: selected?.id || tracks[0]?.id,
            sourceVideoUrl,
            refTable: 'draft',
            refId: draftRef,
            tiktokUseNativeSound: true,
          }
        : {
            trackId: selected.id,
            sourceVideoUrl,
            refTable: 'draft',
            refId: draftRef,
            volume,
            fadeInSec: fadeIn,
            fadeOutSec: fadeOut,
            duckVideoAudio: duckVideo,
            videoAudioVolume: duckVideo ? 0.3 : 1.0,
          };
      console.debug('[MusicPicker] POST /api/music/attach body=', body);
      const { data } = await api.post('/music/attach', body);
      console.debug('[MusicPicker] attach response=', data);
      if (!data?.success) throw new Error(data?.error || 'Attach failed');
      onAttached?.({
        attachmentId: data.attachmentId,
        track: selected,
        params: body,
        tiktokUseNativeSound: !!useTikTokNative,
      });
      onClose?.();
    } catch (err) {
      console.error('[MusicPicker] attach failed', err);
      const apiMsg = err?.response?.data?.error;
      const status = err?.response?.status;
      setErrorMsg(apiMsg ? `${apiMsg}${status ? ` (HTTP ${status})` : ''}` : (err.message || 'Attach failed'));
    } finally {
      setAttaching(false);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    modal: {
      width: 'min(720px, 95vw)', maxHeight: '90vh',
      background: '#1a2240', color: '#fff', borderRadius: 10,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
    },
    header: {
      padding: '14px 18px', background: '#253866',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    },
    body: { padding: '14px 18px', overflowY: 'auto', flex: 1 },
    searchRow: { display: 'flex', gap: 8, marginBottom: 12 },
    searchInput: {
      flex: 1, padding: '8px 10px', background: '#0f1730', color: '#fff',
      border: '1px solid #3a4a7a', borderRadius: 6, fontSize: 14,
    },
    btn: {
      padding: '8px 14px', background: '#ff6800', color: '#fff',
      border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600,
    },
    btnGhost: {
      padding: '8px 14px', background: 'transparent', color: '#fff',
      border: '1px solid #5a6a9a', borderRadius: 6, cursor: 'pointer',
    },
    track: (isSel) => ({
      padding: '10px 12px', borderRadius: 6, marginBottom: 6,
      background: isSel ? '#2c3a6a' : '#0f1730',
      border: isSel ? '2px solid #ff6800' : '1px solid #2a345e',
      cursor: 'pointer',
    }),
    trackTitle: { fontWeight: 600, fontSize: 14, marginBottom: 2 },
    trackMeta: { fontSize: 12, opacity: 0.7 },
    badges: { display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' },
    badge: (state) => ({
      fontSize: 11, padding: '2px 6px', borderRadius: 4,
      background: state === 'safe' ? '#1d5d34' : state === 'may_claim' ? '#7a5a14' : state === 'blocked' ? '#7a1a1a' : '#404870',
    }),
    sliderRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 },
    sliderLabel: { fontSize: 12, minWidth: 110, opacity: 0.85 },
    footer: {
      padding: '12px 18px', background: '#101830',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
    },
    err: { color: '#ff8e8e', fontSize: 13, marginBottom: 10 },
    tiktokBox: {
      padding: 10, marginBottom: 10, borderRadius: 6,
      background: '#1f2748', border: '1px dashed #5a6a9a', fontSize: 13,
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose} role="presentation">
      <div style={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Music picker">
        <div style={styles.header}>
          <strong>Add music</strong>
          <button type="button" onClick={onClose} style={{ ...styles.btnGhost, padding: '4px 10px' }}>Close</button>
        </div>

        <div style={styles.body}>
          {errorMsg && <div style={styles.err}>{errorMsg}</div>}

          {!sourceVideoUrl && (
            <div style={{
              padding: '10px 12px', marginBottom: 12, borderRadius: 6,
              background: '#5a3010', color: '#ffd9b3', border: '1px solid #a05010', fontSize: 13,
            }}>
              ⚠ This post doesn't have a video yet. Music can only be added to video
              posts — add or capture a video first, then come back to attach music.
            </div>
          )}

          {tiktokSelected && (
            <div style={styles.tiktokBox}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={useTikTokNative}
                  onChange={(e) => setUseTikTokNative(e.target.checked)}
                />
                Use a TikTok-native sound instead — On Screen will send the video
                to your TikTok inbox so you can pick a trending track inside the
                TikTok app. Other selected platforms still use the track you choose below.
              </label>
            </div>
          )}

          <div style={styles.searchRow}>
            <input
              style={styles.searchInput}
              placeholder="Search music (e.g. upbeat, ambient, corporate)…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKey}
            />
            <select
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              style={{
                padding: '8px 8px', background: '#0f1730', color: '#fff',
                border: '1px solid #3a4a7a', borderRadius: 6, fontSize: 13, cursor: 'pointer',
              }}
              title="Sort tracks"
            >
              <option value="popular">Most popular</option>
              <option value="trending">Trending this week</option>
              <option value="listens">Most listens</option>
              <option value="newest">Newest</option>
            </select>
            <button type="button" style={styles.btn} onClick={() => runSearch(query)} disabled={loading}>
              {loading ? 'Searching…' : 'Search'}
            </button>
          </div>

          {tracks.length === 0 && !loading && (
            <div style={{ textAlign: 'center', opacity: 0.7, padding: 30 }}>
              No tracks. Try a different keyword.
            </div>
          )}

          {tracks.map((t) => {
            const isSel = selected?.id === t.id;
            const badges = compatBadgesFor(t, selectedPlatforms);
            const dur = t.duration_sec ? `${Math.floor(t.duration_sec / 60)}:${String(t.duration_sec % 60).padStart(2, '0')}` : '—';
            return (
              <div
                key={t.id}
                style={styles.track(isSel)}
                onClick={() => setSelected(t)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') setSelected(t); }}
              >
                <div style={styles.trackTitle}>{t.title}</div>
                <div style={styles.trackMeta}>
                  {t.artist || 'Unknown artist'} · {dur} · {t.license_code} · {t.provider}
                  {Number(t.listens) > 0 && (
                    <> · {Number(t.listens).toLocaleString()} plays</>
                  )}
                </div>
                <div style={styles.badges}>
                  {badges.map((b) => (
                    <span
                      key={b.platform}
                      style={styles.badge(b.state)}
                      title={COMPAT_LABEL[b.state]?.label || b.state}
                    >
                      {COMPAT_LABEL[b.state]?.icon} {b.platform}
                    </span>
                  ))}
                  <button
                    type="button"
                    style={{ ...styles.btnGhost, padding: '2px 8px', fontSize: 11 }}
                    onClick={(e) => { e.stopPropagation(); playPreview(t); }}
                  >▶ Preview</button>
                </div>
              </div>
            );
          })}

          {selected && !useTikTokNative && (
            <div style={{ marginTop: 14, padding: 12, background: '#0f1730', borderRadius: 6 }}>
              <div style={{ fontSize: 13, marginBottom: 6, opacity: 0.85 }}>Mix settings for "{selected.title}"</div>
              <div style={styles.sliderRow}>
                <span style={styles.sliderLabel}>Music volume</span>
                <input type="range" min={0} max={2} step={0.05} value={volume}
                       onChange={(e) => setVolume(Number(e.target.value))} style={{ flex: 1 }} />
                <span style={{ fontSize: 12, minWidth: 40 }}>{volume.toFixed(2)}</span>
              </div>
              <div style={styles.sliderRow}>
                <span style={styles.sliderLabel}>Fade in (s)</span>
                <input type="range" min={0} max={5} step={0.5} value={fadeIn}
                       onChange={(e) => setFadeIn(Number(e.target.value))} style={{ flex: 1 }} />
                <span style={{ fontSize: 12, minWidth: 40 }}>{fadeIn.toFixed(1)}</span>
              </div>
              <div style={styles.sliderRow}>
                <span style={styles.sliderLabel}>Fade out (s)</span>
                <input type="range" min={0} max={5} step={0.5} value={fadeOut}
                       onChange={(e) => setFadeOut(Number(e.target.value))} style={{ flex: 1 }} />
                <span style={{ fontSize: 12, minWidth: 40 }}>{fadeOut.toFixed(1)}</span>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13 }}>
                <input type="checkbox" checked={duckVideo} onChange={(e) => setDuckVideo(e.target.checked)} />
                Duck the video's original audio so music sits on top
              </label>
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            {selected ? `Selected: ${selected.title}` : useTikTokNative ? 'TikTok native sound mode' : 'Pick a track to enable Attach'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={styles.btnGhost} onClick={onClose}>Cancel</button>
            <button
              type="button"
              style={{ ...styles.btn, opacity: attaching ? 0.5 : 1 }}
              onClick={handleAttach}
              disabled={attaching}
            >
              {attaching ? 'Attaching…' : 'Attach music'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
